import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BellIcon } from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'
import { cn } from 'src/utils/style'
import { notificationApi, notificationQueries } from 'src/services/notifications'
import type { Notification } from 'src/services/notifications'
import { useAuth } from 'src/stores/auth'
import { Pagination } from '@thinknimble/tn-models'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/button'

function NotificationItem({
  notification,
  onRead,
}: {
  notification: Notification
  onRead: (n: Notification) => void
}) {
  return (
    <Button
      variant="ghost"
      onClick={() => onRead(notification)}
      className={cn(
        'w-full items-start justify-start gap-3 px-4 py-3 text-left',
        !notification.isRead && 'bg-surface-hover',
      )}
    >
      {!notification.isRead && (
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500" />
      )}
      {notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'truncate text-sm',
            notification.isRead ? 'text-text-secondary' : 'font-medium text-text-primary',
          )}
        >
          {notification.title}
        </p>
        <p className="mt-0.5 line-clamp-2 text-xs text-text-placeholder">{notification.body}</p>
        <span className="mt-1 block text-xs text-text-tertiary">
          {notification.created
            ? formatDistanceToNow(new Date(notification.created), { addSuffix: true })
            : ''}
        </span>
      </div>
    </Button>
  )
}

/**
 * The notification list body: header with "mark all read", the scrollable list
 * with per-item read + relative timestamps, and a "view all" footer. Shared by
 * the side-nav notifications flyout (and previously the floating bell). `onClose`
 * lets the host close its container after a navigation.
 */
export const NotificationList = ({ onClose }: { onClose?: () => void }) => {
  const { t } = useTranslation()
  const token = useAuth.use.token()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: recentNotifications } = useQuery({
    ...notificationQueries.list({
      pagination: new Pagination({ page: 1, size: 8 }),
    }),
    enabled: Boolean(token),
  })

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationApi.csc.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueries.all() })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationApi.csc.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueries.all() })
    },
  })

  const unreadCount = (recentNotifications?.results ?? []).filter((n) => !n.isRead).length

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id)
    }
    onClose?.()
    const url = notification.metadata?.actionUrl
    if (url && typeof url === 'string') {
      navigate(url)
    }
  }

  const notifications = recentNotifications?.results ?? []

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-text-primary">{t('notifications.title')}</h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="text"
              tone="primary"
              onClick={() => markAllReadMutation.mutate()}
              className="text-xs font-medium"
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
      </div>

      <div className="max-h-96 divide-y divide-border overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <BellIcon className="mx-auto h-8 w-8 text-gray-300" aria-hidden="true" />
            <p className="mt-2 text-sm text-text-placeholder">{t('notifications.empty')}</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={handleNotificationClick}
            />
          ))
        )}
      </div>

      {notifications.length > 0 && (
        <div className="border-t border-border">
          <Button
            variant="ghost"
            tone="primary"
            size="sm"
            onClick={() => {
              onClose?.()
              navigate('/notifications')
            }}
            className="w-full font-medium"
          >
            {t('notifications.viewAll')}
          </Button>
        </div>
      )}
    </div>
  )
}
